/**
 * CONFIGURACIÓN DE M SOCCER ACADEMY
 * =============================================================================
 * ESTE ES EL ÚNICO ARCHIVO QUE HAY QUE TOCAR PARA PONER LOS DATOS REALES.
 *
 * Todo lo que aparece entre «PENDIENTE» es un dato que no me diste y que no puedo
 * inventar. Mientras siga así, los enlaces no llevan a ningún lado: es preferible
 * un enlace que avisa a un enlace que manda a la gente a un número que no existe.
 *
 * Al terminarlos, se cambian aquí y no hay que tocar nada más del proyecto.
 */

export const CONFIG = {
  // ===========================================================================
  // IDENTIDAD
  // ===========================================================================
  academia: {
    nombre: 'M SOCCER ACADEMY',
    /** El lema del escudo. Va en mayúsculas, como en las referencias. */
    lema: 'DISCIPLINE CREATES FREEDOM.',
    /** El cierre de la página, en cursiva como en la referencia. */
    firma: 'Más que Fútbol',
  },

  // ===========================================================================
  // CONTACTO
  // ===========================================================================
  // PENDIENTE · No me diste el correo oficial.
  // Mientras diga lo de abajo, el botón de correo abre el cliente con ese
  // destinatario, que no existe. Hay que reemplazarlo.
  correo: 'PENDIENTE@msocceracademy.com',

  /**
   * WHATSAPP: DESACTIVADO A PROPÓSITO.
   *
   * Dijiste que por el momento no se pone. No se inventa un número y no se enseña
   * el botón: un botón que no lleva a ningún lado es peor que no tenerlo.
   *
   * PARA ACTIVARLO MÁS ADELANTE:
   *   1. Pon el número con lada del país y sin espacios ni signos. México: 52 + 10
   *      dígitos. Ejemplo: '528112345678'
   *   2. Cambia `activo` a true.
   *   3. El botón aparece solo en la sección de contacto.
   */
  whatsapp: {
    activo: false,
    /** PENDIENTE · Aquí va el número cuando lo tengas. */
    numero: '',
    /** El mensaje que ya va escrito cuando alguien abre la conversación. */
    mensaje: 'Hola, quiero información sobre las inscripciones de M Soccer Academy.',
  },

  // ===========================================================================
  // UBICACIÓN
  // ===========================================================================
  // PENDIENTE · Estos son los datos que aparecen en tu referencia de contacto.
  // Si son los correctos, no hay que tocarlos; si no, se cambian aquí.
  ubicacion: {
    nombre: 'Harborside Park',
    calle: '670 Oxford Street',
    ciudad: 'Chula Vista, CA 91911',
    pais: {
      es: 'Estados Unidos',
      en: 'United States',
    },
    /**
     * El enlace que abre el mapa. Se armó con la dirección de arriba.
     * Si la dirección cambia, hay que volver a generarlo desde Google Maps:
     * buscar el lugar → Compartir → Copiar enlace.
     */
    mapa: 'https://www.google.com/maps/search/?api=1&query=Harborside+Park+670+Oxford+Street+Chula+Vista+CA+91911',
  },

  // ===========================================================================
  // REDES SOCIALES
  // ===========================================================================
  // PENDIENTE · No me diste los perfiles oficiales. Los enlaces de abajo son
  // búsquedas, no los perfiles: hay que reemplazarlos por las direcciones reales
  // de cada cuenta.
  redes: {
    /**
     * Instagram. Ya está puesto.
     *
     * Se quitó el `?vrfl=...` de la dirección que me diste: es un parámetro de
     * seguimiento del enlace y no forma parte del perfil.
     */
    instagram: 'https://www.instagram.com/msoccer700',
    facebook: 'https://www.facebook.com/Msocceracademy',
    /**
     * TikTok. Ya está puesto.
     *
     * Se quitó el `?_r=1&_t=...` de la dirección que me diste: esos parámetros son
     * de seguimiento del enlace que copiaste y no forman parte del perfil. Con la
     * dirección limpia funciona igual y se ve mejor si alguien la copia.
     */
    tiktok: 'https://www.tiktok.com/@msoccer814',
  },

  // ===========================================================================
  // HORARIOS Y PROGRAMAS
  // ===========================================================================
  // PENDIENTE · No me diste horarios. No se inventan: se deja dicho que están por
  // confirmar, que es la verdad, en lugar de poner unos que no son.
  horarios: {
    confirmados: false,
    /** Cuando tengas los horarios, se llenan aquí. */
    lista: [] as Array<{ dias: string; horas: string; sede: string }>,
    /** Lo que se enseña mientras no estén confirmados. */
    nota: {
      es: 'Horarios por confirmar. Escríbenos y te decimos los del grupo que te corresponde.',
      en: 'Schedules to be confirmed. Write to us and we will tell you the times for the right group.',
    },  },

  // ===========================================================================
  // FORMULARIO
  // ===========================================================================
  // PENDIENTE · No hay servidor configurado.
  //
  // CÓMO CONECTARLO
  //   1. Crea el endpoint donde quieras recibir los datos. Sirve cualquier
  //      servicio que acepte un POST con JSON: un Worker de Cloudflare, Formspree,
  //      Google Forms con apps script, o tu propio servidor.
  //   2. Pega la dirección aquí.
  //   3. Listo: el formulario empieza a enviar solo.
  //
  // QUÉ MANDA
  // Un POST con este cuerpo:
  //   { tutor, jugador, edad, telefono, correo, programa, mensaje, idioma }
  //
  // MIENTRAS NO HAYA ENDPOINT
  // El formulario NO dice «enviado», porque no se envió nada. Dice la verdad: que
  // falta conectar el destino, y ofrece el correo como alternativa real.
  /**
   * LOS AJUSTES QUE SE EDITAN DESDE EL PANEL
   * -----------------------------------------------------------------------------
   * De aquí los lee la página. Lo que se cambie en el panel llega a la web sin
   * tocar código y sin volver a publicar.
   */
  ajustes: {
    api: '/admin/api/config',
  },

  /**
   * LAS NOTIFICACIONES AL TELÉFONO
   * -----------------------------------------------------------------------------
   * El botón para que la gente reciba los avisos de la academia. Los avisos se
   * mandan desde el panel.
   *
   * Para que funcione hacen falta tres cosas, y las tres están puestas:
   *   1 · Un archivo `sw.js` en la raíz del sitio, que es el que recibe los avisos
   *       con la página cerrada.
   *   2 · Un par de claves para firmarlos, que viven como secretos en el servidor.
   *   3 · El servidor, que cifra y manda cada aviso.
   */
  notificaciones: {
    /** Si se muestra el botón en la página. Se puede apagar desde el panel. */
    activa: true,
    /** Donde se pide la clave pública. */
    clave: '/admin/api/avisos/clave',
    /** Donde se apunta el teléfono cuando alguien acepta. */
    suscribir: '/admin/api/avisos/suscribir',
    /** El archivo que recibe los avisos. */
    worker: '/sw.js',
  },

  /**
   * LA GALERÍA
   * -----------------------------------------------------------------------------
   * De dónde salen las fotos que se ven en la página. Las pone el administrador
   * desde el panel, así que aquí no hay que tocar nada.
   */
  galeria: {
    /** La dirección de donde se piden las fotos. */
    api: '/admin/api/galeria',
    /** Si se muestra la sección de galería en la página. */
    activa: true,
  },

  formulario: {
    /**
     * DÓNDE SE MANDAN LAS SOLICITUDES
     *
     * -----------------------------------------------------------------------------
     * YA ESTÁ CONECTADO
     * -----------------------------------------------------------------------------
     * El formulario manda los datos al panel de administración, que los guarda en
     * la base de datos. Desde el panel se ven, se contactan y se cobran.
     *
     * El panel está en:
     *   https://m-soccer-academy.pages.dev/admin
     *
     * Y se entra con el usuario y la contraseña que me diste.
     */
    endpoint: '/admin/api/registro',

    /** El correo al que se pueden escribir si algo falla. */
    correoDestino: 'PENDIENTE@msocceracademy.com',
  },

  // ===========================================================================
  // IDIOMA
  // ===========================================================================
  idiomaPorDefecto: 'es' as 'es' | 'en',

  // ===========================================================================
  // FOTOGRAFÍAS
  // ===========================================================================
  /**
   * LAS FOTOS DE LAS REFERENCIAS NO LAS TENGO.
   *
   * Las imágenes que me mandaste son capturas del diseño ya armado, no los
   * archivos originales de las fotografías. No puedo recortarlas de ahí: saldrían
   * con el texto encima y con la resolución de la captura.
   *
   * Mientras no estén, cada fotografía se sustituye por una ilustración dibujada
   * con vectores —un campo al atardecer— que respeta la composición de la
   * referencia: misma proporción, mismo punto de interés, mismo degradado cálido.
   *
   * PARA PONER LAS TUYAS
   *   1. Guarda la foto en `public/fotos/` con el nombre que quieras.
   *   2. Escribe la ruta aquí, empezando con `./fotos/`.
   *   3. Si dejas el texto vacío, se usa la ilustración.
   *
   * CONSEJOS PARA QUE SE VEAN COMO EN LAS REFERENCIAS
   *   · Que sean horizontales y de al menos 1920 px de ancho.
   *   · Que el sujeto esté a la derecha en las portadas: el texto va a la izquierda.
   *   · Que tengan luz cálida de atardecer y fondo oscuro.
   */
  fotos: {
    /**
     * LAS FOTOGRAFÍAS ORIGINALES, LIMPIAS.
     *
     * -----------------------------------------------------------------------------
     * QUÉ CAMBIÓ
     * -----------------------------------------------------------------------------
     * Antes había recortes de las capturas de referencia. Como en una captura el
     * texto y la fotografía son los mismos píxeles, esos recortes eran tiras
     * estrechas y algunas traían letras pegadas en el borde.
     *
     * Ahora son los archivos originales, sin una sola letra, a resolución completa.
     * Eso permite que cada sección tenga la misma presencia que la portada.
     *
     * -----------------------------------------------------------------------------
     * CÓMO ESTÁN TRATADAS
     * -----------------------------------------------------------------------------
     * Están tal como las mandaste. El oscurecido y el viñeteado NO se hornean en el
     * archivo: se aplican con CSS, en `.foto-marco::after` y en el velo de la
     * portada. Así, si algún día cambias una foto, hereda el mismo tratamiento sola
     * y no hay que volver a procesarla.
     *
     * -----------------------------------------------------------------------------
     * MEDIDAS
     * -----------------------------------------------------------------------------
     * Las verticales son 900x1600 (relación 9:16) y las horizontales 1600x900
     * (16:9). El componente decide el encuadre con `object-position`, así que no hay
     * que recortarlas a mano: la misma foto sirve en el teléfono y en el escritorio.
     *
     * Si quieres cambiar una, guarda el archivo en `public/fotos/` y cambia la ruta
     * aquí empezando con `./fotos/`.
     */
    portadaInicio: './fotos/panel-cancha.jpg',
    portadaAcademia: './fotos/hero-jugadora-logo.jpg',
    portadaMetodologia: './fotos/hero-nino-conos.jpg',
    portadaProgramas: './fotos/hero-nino-balon.jpg',
    portadaCompetencias: './fotos/panel-trofeo.jpg',
    portadaInformacion: './fotos/hero-pareja-logo.jpg',
    portadaContacto: './fotos/hero-nino-torneo.jpg',
    academiaMision: './fotos/hero-jugadora-logo.jpg',
  },
};

/**
 * ¿Está este dato todavía por poner?
 *
 * Sirve para que la página pueda AVISAR en lugar de fallar en silencio. Un enlace
 * que manda a un número inexistente es peor que un enlace que dice «falta
 * configurar».
 */
export function estaPendiente(valor: string | undefined | null): boolean {
  if (!valor) return true;
  return valor.toUpperCase().includes('PENDIENTE');
}

/** Los datos que faltan, para poder enseñarlos juntos en un aviso. */
export function datosPendientes(): string[] {
  const faltan: string[] = [];

  if (estaPendiente(CONFIG.correo)) faltan.push('Correo oficial');
  if (estaPendiente(CONFIG.formulario.correoDestino)) {
    faltan.push('Correo para recibir las solicitudes');
  }
  if (!CONFIG.formulario.endpoint) faltan.push('Dirección del formulario (endpoint)');
  if (!CONFIG.horarios.confirmados) faltan.push('Horarios de los grupos');

  // Las redes: se comprueba que no sean solo la página principal del servicio.
  const redesSimples = Object.values(CONFIG.redes).some(
    (u) => u.endsWith('.com/') || u.endsWith('.com')
  );
  if (redesSimples) faltan.push('Perfiles oficiales de Instagram, Facebook y TikTok');

  const sinFoto = Object.values(CONFIG.fotos).filter((f) => !f).length;
  if (sinFoto > 0) {
    faltan.push(`Fotografías originales (faltan ${sinFoto})`);
  }

  return faltan;
}
